import React, { createContext, useMemo, useContext, useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import { BASEURL, AUTH } from "config/config";

interface Auth {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<Auth>({
  isAuthenticated: false,
  user: null,
  loading: true,
  checkAuth: async () => {},
  logout: async () => {},
});

// CSRF 토큰 가져오기 함수
const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === '_csrf') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

export const AuthProvider = ({ children }: { children: React.ReactElement | React.ReactElement[] }): React.ReactElement => {
  const toast = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // axios 기본 설정
  axios.defaults.withCredentials = true;
  axios.defaults.baseURL = BASEURL;

  // CSRF 토큰 인터셉터 설정
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const csrfToken = getCSRFToken();
        if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => axios.interceptors.request.eject(requestInterceptor);
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(AUTH.ME);
      setUser(response.data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        setUser(null);
      } else {
        console.error('Auth check failed:', error);
        toast({
          title: "인증 확인 실패",
          description: "세션 확인 중 오류가 발생했습니다.",
          status: "error",
          duration: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(AUTH.LOGOUT);
      setUser(null);
      toast({
        title: "로그아웃 완료",
        description: "성공적으로 로그아웃되었습니다.",
        status: "success",
        duration: 2000,
      });
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
      window.location.href = '/login';
    }
  };

  const isAuthenticated = useMemo(() => !!user, [user]);

  useEffect(() => {
    checkAuth();
  }, []);

  // axios 응답 인터셉터
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
          setUser(null);
          toast({
            title: "세션 만료",
            description: "다시 로그인해주세요.",
            status: "warning",
            duration: 3000,
          });
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [toast]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      loading,
      checkAuth,
      logout,
    }),
    [user, isAuthenticated, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): Auth => {
  const ctx = useContext(AuthContext);
  return ctx;
};
